import { useEffect, useRef, useState } from 'react'
import { Icon } from '@nimbus-ds/components'
import { CopyIcon, CheckIcon } from '@nimbus-ds/icons'

/**
 * Copy a campaign's identifier so it can be pasted into the ads manager.
 *
 * This is the one action the map and the list genuinely owe the person reading them. We
 * deliberately don't integrate with Meta/Google (out of scope), so a finding about
 * «120257149267010550» is only actionable if that string can get from our screen into the
 * ads manager's search box. Retyping 18 digits off a chart is exactly where someone gives up.
 *
 * It lives in the LIST, not in the chart tooltip: the tooltip is `pointer-events-none` so
 * that moving the mouse toward it doesn't dismiss the bubble it describes — nothing inside
 * it can be clicked, by construction.
 */
/**
 * Put a string on the clipboard. Resolves false if the browser refused, so no caller ever
 * claims a copy that didn't happen.
 *
 * `navigator.clipboard` needs a secure context and is missing on older in-app webviews —
 * merchants open this from inside the TiendaNube admin, so that case is real, not theoretical.
 */
export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
    const ta = document.createElement('textarea')
    ta.value = value
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

export function CopyCampaign({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const copy = async (e: React.MouseEvent) => {
    // These rows sit inside <Link> wrappers on some surfaces; copying must not navigate.
    e.preventDefault()
    e.stopPropagation()
    const ok = await copyText(value)
    setCopied(ok)
    if (ok) {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 1600)
    }
  }

  // Icon only. These sit on every campaign row, so a "Copiar" word next to each one stacked
  // up into a column of blue noise louder than the data. The tooltip carries the meaning, the
  // aria-label carries it for screen readers, and the tick carries the confirmation.
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copiado' : `Copiar «${value}» para buscarla en tu administrador de anuncios`}
      aria-label={copied ? `${value} copiado` : `Copiar ${value}`}
      className={`inline-flex h-5 w-5 shrink-0 translate-y-[1px] items-center justify-center rounded align-middle ${
        copied ? 'text-[#00935b]' : 'text-[#b0b0b0] hover:bg-[#f0f0f0] hover:text-[#5d5d5d]'
      }`}
    >
      <Icon source={copied ? <CheckIcon /> : <CopyIcon />} color="currentColor" />
    </button>
  )
}
